/**
 * Pictures for "Where will you stand?", drawn in code like the quiz portraits so okjapa loads nothing extra.
 * Used by okjapa's preview and Voucha's result page. Colours come from the --vs-* variables when set.
 */
import { portrait } from './portraits';
export const RUNG_KEYS = ['highest', 'upper-middle', 'middle', 'lower-middle', 'lowest'];
export const RUNG_LABELS = {
  highest: 'Highest',
  'upper-middle': 'Upper-middle',
  middle: 'Middle',
  'lower-middle': 'Lower-middle',
  lowest: 'Lowest'
};
const RUNG_COLOURS = {
  highest: 'var(--vs-highest,#1f6f4a)',
  'upper-middle': 'var(--vs-upper,#3f9b6b)',
  middle: 'var(--vs-middle,#2f8f9d)',
  'lower-middle': 'var(--vs-lower,#e0a43a)',
  lowest: 'var(--vs-lowest,#e06b4f)'
};
export const rungColour = key => RUNG_COLOURS[key] || RUNG_COLOURS.middle;
const INK = 'var(--vs-ink,#1b2b28)';
const MUTED = 'var(--vs-muted,#6b7a76)';
const LINE = 'var(--vs-line,#d6dfdc)';
const esc = s => String(s).replace(/[&<>"]/g, c => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;'
})[c]);

/** a portrait as a nested picture at x, y (top-left), w wide */
function face(name, sex, x, y, w) {
  const svg = portrait(name, sex);
  if (!svg) return '';
  return svg.replace('<svg class="VPortrait"', `<svg x="${x}" y="${y}" width="${w}" height="${w * 150 / 160}"`);
}

/**
 * Two ladders side by side: home and there. marks: [{ladder: 0|1, rung, label}] (label under the person).
 * The person's portrait stands on the first mark of each ladder.
 */
export function ladders(_ref) {
  let {
    titles,
    marks,
    sex
  } = _ref;
  const W = 320,
    H = 300,
    top = 70,
    step = 44,
    rungW = 90;
  const xs = [16, 214];
  let svg = `<svg class="VStanding-ladders" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${esc(marks.map(m => titles[m.ladder] + ': ' + RUNG_LABELS[m.rung.key || m.rung] + ' (' + m.label + ')').join('; '))}">`;
  xs.forEach((x, li) => {
    svg += `<text x="${x + rungW / 2}" y="${H - 6}" text-anchor="middle" font-size="13" font-weight="700" fill="${INK}">${esc(titles[li])}</text>`;
    // rails
    svg += `<path d="M${x} ${top - 6}V${top + step * 4 + 14}M${x + rungW} ${top - 6}V${top + step * 4 + 14}" stroke="${LINE}" stroke-width="4" stroke-linecap="round"/>`;
    RUNG_KEYS.forEach((k, i) => {
      const y = top + step * i + 10;
      svg += `<rect x="${x}" y="${y - 4}" width="${rungW}" height="8" rx="4" fill="${rungColour(k)}" opacity="0.9"/>`;
      if (li === 0) svg += `<text x="${W / 2 + 2}" y="${y + 4}" text-anchor="middle" font-size="10.5" fill="${MUTED}">${RUNG_LABELS[k]}</text>`;
    });
  });
  // marks: a dot on the rung, the label beside; the first mark on each ladder gets the person
  const seen = {};
  const perRung = {};
  marks.forEach(m => {
    const key = m.rung.key || m.rung;
    const i = RUNG_KEYS.indexOf(key);
    if (i < 0) return;
    const x = xs[m.ladder];
    const y = top + step * i + 10;
    const slot = (perRung[m.ladder + key] = (perRung[m.ladder + key] || 0) + 1) - 1;
    if (!seen[m.ladder] && sex) {
      seen[m.ladder] = true;
      svg += face('you-{sex}', sex, x + 30 + slot * 30, y - 46, 44);
    } else {
      svg += `<circle cx="${x + 18 + slot * 34}" cy="${y - 12}" r="7" fill="#fff" stroke="${rungColour(key)}" stroke-width="3"/>`;
    }
    svg += `<text x="${x + rungW / 2}" y="${y + 22}" text-anchor="middle" font-size="10.5" font-weight="700" fill="${INK}">${esc(m.label)}</text>`;
  });
  return svg + '</svg>';
}

/** Ten small people coloured by the rung each lands on (ten = rung keys, lowest first) */
export function tenPeople(ten) {
  const W = 320,
    H = 64,
    gap = 32;
  let svg = `<svg class="VStanding-ten" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${esc(countText(ten))}">`;
  ten.forEach((k, i) => {
    const x = 16 + i * gap;
    const c = rungColour(k);
    svg += `<circle cx="${x}" cy="16" r="8" fill="${c}"/><path d="M${x - 11} 52c0-14 5-22 11-22s11 8 11 22z" fill="${c}"/>`;
  });
  return svg + '</svg>';
}

/** "6 Middle, 4 Lower-middle" */
export function countText(ten) {
  const counts = {};
  ten.forEach(k => counts[k] = (counts[k] || 0) + 1);
  return RUNG_KEYS.filter(k => counts[k]).map(k => counts[k] + ' ' + RUNG_LABELS[k]).join(', ');
}

/** A legend for the rung colours that appear */
export function legend(keys) {
  return RUNG_KEYS.filter(k => keys.includes(k)).map(k => ({
    key: k,
    label: RUNG_LABELS[k],
    colour: rungColour(k)
  }));
}

/** "Top 4%", "the middle 65%", "the bottom 13%": where a tier sits, from the shares (highest first) */
export function placeText(shares, key) {
  if (!shares || !key) return '';
  const pct = n => (n < 1 ? 'under 1' : String(Math.round(n))) + '%';
  const i = RUNG_KEYS.indexOf(key);
  if (key === 'middle') return 'the middle ' + pct(shares.middle);
  if (i < 2) return 'the top ' + pct(RUNG_KEYS.slice(0, i + 1).reduce((n, k) => n + shares[k], 0));
  return 'the bottom ' + pct(RUNG_KEYS.slice(i).reduce((n, k) => n + shares[k], 0));
}

/**
 * Two shapes drawn to scale: each tier's width is its share of households. You stand on your tier in each.
 * shares: [home, there] ({tier: percent}); you: [homeTier|null, thereTier]; titles: [home, there].
 */
export function shapes(_ref2) {
  let {
    titles,
    shares,
    you,
    sex,
    notes
  } = _ref2;
  const W = 350,
    rowH = 40,
    barH = 26,
    top = 12,
    maxW = 132,
    scale = maxW / 80;
  const H = top + rowH * 5 + 30 + (notes ? 30 : 0);
  const cx = [86, 264];
  const label = n => n < 1 ? '<1%' : Math.round(n) + '%';
  let svg = `<svg class="VStanding-shapes" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${esc(titles.map((t, i) => you[i] ? t + ': ' + RUNG_LABELS[you[i]] + ', ' + placeText(shares[i], you[i]) : '').filter(Boolean).join('; '))}">`;
  RUNG_KEYS.forEach((k, r) => {
    const y = top + r * rowH;
    svg += `<text x="${W / 2}" y="${y + barH / 2 + 4}" text-anchor="middle" font-size="10.5" fill="${MUTED}">${RUNG_LABELS[k]}</text>`;
  });
  [0, 1].forEach(c => {
    const s = shares[c];
    if (!s) return;
    RUNG_KEYS.forEach((k, r) => {
      const y = top + r * rowH;
      const w = Math.max(6, s[k] * scale);
      const x = cx[c] - w / 2;
      const mine = you[c] === k;
      const outer = c === 0 ? -1 : 1; // which way is away from the labels
      svg += `<rect x="${x.toFixed(1)}" y="${y}" width="${w.toFixed(1)}" height="${barH}" rx="6" fill="${rungColour(k)}" opacity="${you[c] && !mine ? 0.45 : 1}"/>`;
      const inside = w >= 38;
      const tx = inside ? cx[c] : cx[c] + outer * (w / 2 + 16);
      svg += `<text x="${tx.toFixed(1)}" y="${y + barH / 2 + 4}" text-anchor="middle" font-size="11" font-weight="700" fill="${inside ? '#fff' : INK}">${label(s[k])}</text>`;
      if (mine && sex) {
        // the person stands at the outer end of the bar (inside a wide bar, beside a narrow one)
        const fw = 34;
        const fx = inside && w > 90 ? c === 0 ? x + 2 : x + w - fw - 2 : c === 0 ? x - fw - 30 : x + w + 30;
        svg += face('you-{sex}', sex, Math.max(0, Math.min(W - fw, fx)), y - 6, fw);
      }
    });
    svg += `<text x="${cx[c]}" y="${top + rowH * 5 + 12}" text-anchor="middle" font-size="13" font-weight="700" fill="${INK}">${esc(titles[c])}</text>`;
  });
  // notes: one line each
  if (notes) [].concat(notes).forEach((n, i, all) => svg += `<text x="${W / 2}" y="${H - 4 - (all.length - 1 - i) * 13}" text-anchor="middle" font-size="10" fill="${MUTED}">${esc(n)}</text>`);
  return svg + '</svg>';
}

/**
 * The money scale: one column in the destination's currency, the tier edges for a household this size,
 * and your household at years 1, 5 and 10. fmt(amount) formats money.
 */
export function moneyScale(_ref3) {
  let {
    scale,
    fmt
  } = _ref3;
  const W = 360,
    top = 16,
    colX = 186,
    colW = 44,
    H = 330;
  const youMax = Math.max(...scale.you.map(y => y.amount));
  const max = Math.max(scale.edges.highest * 1.25, youMax * 1.1);
  const Y = v => top + (H - top - 16) * (1 - v / max);
  const bands = [['highest', scale.edges.highest, max], ['upper-middle', scale.edges['upper-middle'], scale.edges.highest], ['middle', scale.edges.middle, scale.edges['upper-middle']], ['lower-middle', scale.edges['lower-middle'], scale.edges.middle], ['lowest', 0, scale.edges['lower-middle']]];
  let svg = `<svg class="VStanding-scale" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${esc('Where each tier starts for a household of ' + scale.household + ': ' + bands.slice(0, 4).map(_ref4 => {
    let [k, from] = _ref4;
    return RUNG_LABELS[k] + ' from ' + fmt(from);
  }).join(', '))}">`;
  bands.forEach(_ref5 => {
    let [k, from, to] = _ref5;
    svg += `<rect x="${colX}" y="${Y(to).toFixed(1)}" width="${colW}" height="${(Y(from) - Y(to)).toFixed(1)}" fill="${rungColour(k)}"/>`;
    const mid = (Y(from) + Y(to)) / 2;
    svg += `<text x="${colX + colW / 2}" y="${(mid + 3.5).toFixed(1)}" text-anchor="middle" font-size="9.5" font-weight="700" fill="#fff">${k === 'upper-middle' ? 'Upper' : k === 'lower-middle' ? 'Lower' : RUNG_LABELS[k]}</text>`;
  });
  // edges and the median, on the left
  [['Highest from', scale.edges.highest], ['Upper-middle from', scale.edges['upper-middle']], ['Median', scale.median], ['Middle from', scale.edges.middle], ['Lower-middle from', scale.edges['lower-middle']]].forEach(_ref6 => {
    let [t, v] = _ref6;
    const y = Y(v);
    svg += `<path d="M${colX - 6} ${y.toFixed(1)}H${colX}" stroke="${INK}" stroke-width="1.5"/>`;
    svg += `<text x="${colX - 10}" y="${(y + 3.5).toFixed(1)}" text-anchor="end" font-size="10.5" fill="${MUTED}">${t} <tspan font-weight="700" fill="${INK}">${esc(fmt(v))}</tspan></text>`;
  });
  // you, on the right (labels nudged apart when close)
  let last = -99;
  scale.you.slice().sort((a, b) => b.amount - a.amount).forEach(y => {
    const yy = Y(y.amount);
    const ty = Math.max(yy + 4, last + 24);
    last = ty;
    svg += `<path d="M${colX + colW} ${yy.toFixed(1)}H${colX + colW + 12}L${colX + colW + 20} ${(ty - 4).toFixed(1)}H${colX + colW + 26}" fill="none" stroke="${INK}" stroke-width="1.2"/>`;
    svg += `<circle cx="${colX + colW}" cy="${yy.toFixed(1)}" r="3.5" fill="${INK}"/>`;
    svg += `<text x="${colX + colW + 30}" y="${(ty - 1).toFixed(1)}" font-size="10.5" font-weight="700" fill="${INK}">${scale.household > 1 ? 'Household' : 'You'}, year ${y.year}</text>`;
    svg += `<text x="${colX + colW + 30}" y="${(ty + 11).toFixed(1)}" font-size="10.5" fill="${MUTED}">${esc(fmt(y.amount))}</text>`;
  });
  return svg + '</svg>';
}
flarum.reg.add('voucha-assessments', 'common/standing', { RUNG_KEYS: RUNG_KEYS,RUNG_LABELS: RUNG_LABELS,rungColour: rungColour,ladders: ladders,tenPeople: tenPeople,countText: countText,legend: legend,placeText: placeText,shapes: shapes,moneyScale: moneyScale, });